import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Icon from '../AppIcon';

/**
 * Lightweight, dependency-free toast system.
 *
 * Usage:
 *   import { useToast } from 'components/ui/Toast';
 *   const toast = useToast();
 *   toast.success('Saved.');
 *   toast.error('Could not save.', { duration: 6000 });
 *   toast.info('Heads up.', { action: { label: 'Undo', onClick: () => ... } });
 *
 * Wrap the app once with <ToastProvider> (already wired in App.jsx).
 */

const ToastContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
};

const TOAST_TONES = {
  success: {
    icon: 'CheckCircle2',
    classes: 'bg-white border-emerald-100 text-gray-900',
    accent: 'text-emerald-600',
  },
  error: {
    icon: 'AlertCircle',
    classes: 'bg-white border-rose-100 text-gray-900',
    accent: 'text-rose-600',
  },
  info: {
    icon: 'Info',
    classes: 'bg-white border-gray-200 text-gray-900',
    accent: 'text-gray-700',
  },
  warning: {
    icon: 'AlertTriangle',
    classes: 'bg-white border-amber-100 text-gray-900',
    accent: 'text-amber-600',
  },
};

let nextId = 1;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (kind, message, opts = {}) => {
      const id = nextId++;
      const duration = opts.duration ?? 4000;
      setToasts((t) => [...t, { id, kind, message, action: opts.action }]);
      if (duration > 0) {
        const handle = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, handle);
      }
      return id;
    },
    [dismiss],
  );

  const api = useRef({
    success: (m, o) => push('success', m, o),
    error:   (m, o) => push('error',   m, o),
    info:    (m, o) => push('info',    m, o),
    warning: (m, o) => push('warning', m, o),
    dismiss,
  }).current;

  useEffect(() => {
    return () => {
      timers.current.forEach((h) => clearTimeout(h));
      timers.current.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Stack — top-right on desktop, top-center on mobile */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="fixed z-[1000] top-20 inset-x-4 sm:inset-x-auto sm:right-6 sm:left-auto flex flex-col gap-2 pointer-events-none"
      >
        {toasts.map((t) => {
          const tone = TOAST_TONES[t.kind] || TOAST_TONES.info;
          return (
            <div
              key={t.id}
              role={t.kind === 'error' ? 'alert' : 'status'}
              className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-2xl border shadow-[0_8px_28px_-12px_rgba(17,24,39,0.18)] min-w-[280px] sm:max-w-sm ${tone.classes} animate-in slide-in-from-top-2 fade-in duration-200`}
            >
              <Icon name={tone.icon} size={18} className={`mt-0.5 shrink-0 ${tone.accent}`} />
              <div className="flex-1 text-sm leading-snug">{t.message}</div>
              {t.action && (
                <button
                  onClick={() => { t.action.onClick(); dismiss(t.id); }}
                  className="text-sm font-semibold text-rose-600 hover:underline shrink-0"
                >
                  {t.action.label}
                </button>
              )}
              <button
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
                className="shrink-0 text-gray-400 hover:text-gray-700 transition"
              >
                <Icon name="X" size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};
